import { getActiveClient } from 'shared/store/clients/selectors'
import { emptySplitApi, RootState } from 'shared/store/store'
import { axiosErrorMessageHelper } from 'shared/utils/axiosErrorMessageHelper'
import {
  BBLDocumentQueryModel,
  BoardDocumentQueryModel,
} from './models/BoardDocumentQueryModel'
import { BBLDocumentResponse } from './models/BBLDocumentResponse'
import { ArchiveDocumentTypeModel } from './models/BBLDocumentTypeModel'
import { BblDocument } from './models/BblDocumentModel'
import { BoardDocument } from './models/BoardDocumentModel'
import { BoardDocumentUpdateModel } from './models/BoardDocumentUpdateModel'
import {
  GET_BOARD_DOCUMENT_DETAILS,
  GET_BOARD_DOCUMENTS,
  GET_BOARD_DOCUMENTS_WITH_SEARCHTEXT,
} from 'shared/constants/storeTags'
import { LabelAvailableType, LabelModel } from './models/LabelModel'
import { LabelCreateModel } from './models/LabelCreateModel'
import { LabelUpdateModel } from './models/LabelUpdateModel'
import { GET_LABELS } from 'shared/constants/storeTags'
import { annotateLabelsWithHierarchy } from 'Modules/Boardwork/Labels/shared/utils/labelsTree'
import { BoardDocumentResponse } from './models/BoardDocumentResponse'
import { LabelUsageStatsModel } from './models/LabelUsageStatsModel'
import { BoardDocumentSearchModel } from './models/BoardDocumentSearchModel'
import { BoardDocumentSearchResultsModel } from './models/BoardDocumentSearchResultsModel'
import { BoardDocumentLabelUnitChangeModel } from './models/BoardDocumentLabelUnitChangeModel'
import { ProsjektarkivDocumentQueryModel } from './models/prosjektarkiv/ProsjektarkivQueryModel'
import { ProsjektarkivDocumentQueryResponse } from './models/prosjektarkiv/ProsjektarkivQueryResponseModel'
import { ProsjektarkivDocumentSearchResultsModel } from './models/prosjektarkiv/ProsjektarkivSearchResponseModel'
import { ProsjektarkivDocumentSearchModel } from './models/prosjektarkiv/ProsjektarkivSearchModel'
import { ProsjektarkivDocument } from './models/prosjektarkiv/ProsjektarkivDocumentModel'
import { ProsjektarkivDocumentType } from './models/prosjektarkiv/ProsjektarkivDocumentType'
const baseUrl = 'portaldocuments'

const portalDocumentsService = emptySplitApi.injectEndpoints({
  endpoints: (build) => ({
    queryBoardDocuments: build.query<
      BoardDocumentResponse,
      BoardDocumentQueryModel
    >({
      providesTags: [GET_BOARD_DOCUMENTS],
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url:
            baseUrl +
            `/clients/${client.id}/boarddocuments/query
`,
          method: 'POST',
          headers: { 'x-api-version': '1.0' },
          data: args,
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError:
                'Det skjedde en feil ved henting av styrets dokumenter',
            }),
          }
        }

        return {
          data: result.data as BoardDocumentResponse,
        }
      },
    }),
    queryBoardDocumentsWithSearchText: build.query<
      BoardDocumentSearchResultsModel,
      BoardDocumentSearchModel
    >({
      providesTags: [GET_BOARD_DOCUMENTS_WITH_SEARCHTEXT],
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url:
            baseUrl +
            `/clients/${client.id}/boarddocuments/search
`,
          method: 'POST',
          headers: { 'x-api-version': '1.0' },
          data: args,
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError:
                'Det skjedde en feil ved henting av styrets dokumenter',
            }),
          }
        }

        return {
          data: result.data as BoardDocumentSearchResultsModel,
        }
      },
    }),

    queryProsjektarkivDocuments: build.query<
      ProsjektarkivDocumentQueryResponse,
      ProsjektarkivDocumentQueryModel
    >({
      providesTags: [GET_BOARD_DOCUMENTS],
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url:
            baseUrl +
            `/clients/${client.id}/prosjektarkivdocuments/query
`,
          method: 'POST',
          headers: { 'x-api-version': '1.0' },
          data: args,
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError:
                'Det skjedde en feil ved henting av prosjektarkiv dokumenter',
            }),
          }
        }

        return {
          data: result.data as ProsjektarkivDocumentQueryResponse,
        }
      },
    }),
    queryProsjektarkivDocumentsWithSearchText: build.query<
      ProsjektarkivDocumentSearchResultsModel,
      ProsjektarkivDocumentSearchModel
    >({
      providesTags: [GET_BOARD_DOCUMENTS_WITH_SEARCHTEXT],
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url:
            baseUrl +
            `/clients/${client.id}/prosjektarkivdocuments/search
`,
          method: 'POST',
          headers: { 'x-api-version': '1.0' },
          data: args,
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError:
                'Det skjedde en feil ved henting av prosjekt arkiv dokumenter (/search)',
            }),
          }
        }

        return {
          data: result.data as ProsjektarkivDocumentSearchResultsModel,
        }
      },
    }),

    queryBBLDocuments: build.query<BBLDocumentResponse, BBLDocumentQueryModel>({
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url:
            baseUrl +
            `/clients/${client.id}/bbldocuments/query
  `,
          method: 'POST',
          headers: { 'x-api-version': '1.0' },
          data: args,
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError:
                'Det skjedde en feil ved henting av dokumenter delt av forretningsfører',
            }),
          }
        }

        return {
          data: result.data as BBLDocumentResponse,
        }
      },
    }),
    downloadBoardDocument: build.query<Blob, { documentId: string }>({
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url:
            baseUrl +
            `/clients/${client.id}/boarddocuments/${args.documentId}/download`,
          method: 'GET',
          headers: { 'x-api-version': '1.0' },
          responseType: 'arraybuffer',
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError: 'Det skjedde en feil ved henting av dokument',
            }),
          }
        }
        const blob = new Blob([result.data as Blob], {
          type: 'application/pdf',
        })
        return {
          data: blob,
        }
      },
    }),

    downloadProsjektarkivDocument: build.query<Blob, { documentId: string }>({
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url:
            baseUrl +
            `/clients/${client.id}/prosjektarkivdocuments/${args.documentId}/download`,
          method: 'GET',
          headers: { 'x-api-version': '1.0' },
          responseType: 'arraybuffer',
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError: 'Det skjedde en feil ved henting av dokument',
            }),
          }
        }
        const blob = new Blob([result.data as Blob], {
          type: 'application/pdf',
        })
        return {
          data: blob,
        }
      },
    }),

    downloadBBLDocument: build.query<
      Blob,
      { documentId: string; fileId: string }
    >({
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url:
            baseUrl +
            `/clients/${client.id}/bbldocuments/${args.documentId}/files/${args.fileId}`,
          method: 'GET',
          headers: { 'x-api-version': '1.0' },
          responseType: 'arraybuffer',
        })
        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError: 'Det skjedde en feil ved henting av dokument',
            }),
          }
        }
        const blob = new Blob([result.data as Blob], {
          type: 'application/pdf',
        })
        return {
          data: blob,
        }
      },
    }),

    getBBLDocumentTypes: build.query<ArchiveDocumentTypeModel[], void>({
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url: baseUrl + `/documenttypes`,
          method: 'GET',
          headers: { 'x-api-version': '1.0' },
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError: 'Det skjedde en feil ved henting av dokumenttyper',
            }),
          }
        }
        return {
          data: result.data as ArchiveDocumentTypeModel[],
        }
      },
    }),

    getProsjektarkivDocumentTypes: build.query<
      ProsjektarkivDocumentType[],
      void
    >({
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url: `${baseUrl}/clients/${client.id}/prosjektarkivdocuments/documenttypes`,
          method: 'GET',
          headers: { 'x-api-version': '1.0' },
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError: 'Det skjedde en feil ved henting av dokumenttyper',
            }),
          }
        }
        return {
          data: result.data as ProsjektarkivDocumentType[],
        }
      },
    }),

    getBblDocument: build.query<BblDocument, { documentId: string }>({
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url:
            baseUrl + `/clients/${client.id}/bbldocuments/${args.documentId}`,
          method: 'GET',
          headers: { 'x-api-version': '1.0' },
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError: 'Det skjedde en feil ved henting av dokument',
            }),
          }
        }
        return {
          data: result.data as BblDocument,
        }
      },
    }),
    getFilePageCount: build.query<
      { pageCount: number },
      { documentId: string; fileId: string }
    >({
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url:
            baseUrl +
            `/clients/${client.id}/bbldocuments/${args.documentId}/files/${args.fileId}/pages/count`,
          method: 'GET',
          headers: { 'x-api-version': '1.0' },
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError: 'Det skjedde en feil ved henting av dokument',
            }),
          }
        }
        return {
          data: result.data as { pageCount: number },
        }
      },
    }),
    getFilePagePreview: build.query<
      Blob,
      { documentId: string; fileId: string; pageNo: number }
    >({
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url:
            baseUrl +
            `/clients/${client.id}/bbldocuments/${args.documentId}/files/${args.fileId}/pages/${args.pageNo}/preview`,
          method: 'GET',
          headers: { 'x-api-version': '1.0' },
          responseType: 'blob',
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError: 'Det skjedde en feil ved henting av dokument',
            }),
          }
        }
        return {
          data: result.data as Blob,
        }
      },
    }),
    getBBLDocumentsNewCount: build.query<number, void>({
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url: baseUrl + `/clients/${client.id}/bbldocuments/newdocumentscount`,
          method: 'GET',
          headers: { 'x-api-version': '1.0' },
        })
        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError:
                'Det skjedde en feil ved henting av antall nye dokumenter',
            }),
          }
        }
        return {
          data: result.data as number,
        }
      },
    }),
    uploadBBLDocument: build.mutation<boolean, FormData>({
      invalidatesTags: [
        GET_BOARD_DOCUMENTS,
        GET_BOARD_DOCUMENTS_WITH_SEARCHTEXT,
      ],
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url: baseUrl + `/clients/${client.id}/boarddocuments`,
          method: 'POST',
          headers: { 'x-api-version': '1.0' },
          data: args,
        })

        if (result.error) {
          return {
            data: false,
          }
        }
        return {
          data: true,
        }
      },
    }),
    getBoardDocument: build.query<BoardDocument, { documentId: string }>({
      providesTags: [GET_BOARD_DOCUMENT_DETAILS],
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url:
            baseUrl + `/clients/${client.id}/boarddocuments/${args.documentId}`,
          method: 'GET',
          headers: { 'x-api-version': '1.0' },
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError: 'Det skjedde en feil ved henting av dokument',
            }),
          }
        }
        return {
          data: result.data as BoardDocument,
        }
      },
    }),

    getProsjektarkivDocument: build.query<
      ProsjektarkivDocument,
      { documentId: string }
    >({
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url:
            baseUrl +
            `/clients/${client.id}/prosjektarkivdocuments/${args.documentId}`,
          method: 'GET',
          headers: { 'x-api-version': '1.0' },
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError: 'Det skjedde en feil ved henting av dokument',
            }),
          }
        }
        return {
          data: result.data as ProsjektarkivDocument,
        }
      },
    }),

    updateBoardDocument: build.mutation<
      boolean,
      { documentId: string; data: BoardDocumentUpdateModel }
    >({
      invalidatesTags: [GET_BOARD_DOCUMENT_DETAILS],
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url:
            baseUrl + `/clients/${client.id}/boarddocuments/${args.documentId}`,
          method: 'PUT',
          headers: { 'x-api-version': '1.0' },
          data: args.data,
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError: 'Det skjedde en feil ved oppdatering av dokument',
            }),
          }
        }
        return {
          data: true,
        }
      },
    }),
    getLabels: build.query<
      LabelModel[],
      { availableId?: LabelAvailableType } | void
    >({
      providesTags: [GET_LABELS],
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)
        if (!client) throw Error('No client available')
        const availableIdsUrlEnding =
          args && args.availableId ? `?availableId=${args.availableId}` : ''
        const response = await query({
          url: baseUrl + `/clients/${client.id}/labels` + availableIdsUrlEnding,
          method: 'GET',
          headers: { 'x-api-version': '1.0' },
        })
        if (response.error) {
          return {
            error: axiosErrorMessageHelper(response.error, {
              fallbackError: 'Det skjedde en feil ved henting av etiketter',
            }),
          }
        }
        const raw = response.data as LabelModel[]
        const annotated = annotateLabelsWithHierarchy(raw, 0)
        return { data: annotated }
      },
    }),
    createLabel: build.mutation<LabelModel, LabelCreateModel>({
      invalidatesTags: [GET_LABELS],
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)
        if (!client) throw Error('No client available')
        const response = await query({
          url: baseUrl + `/clients/${client.id}/labels`,
          method: 'POST',
          headers: { 'x-api-version': '1.0' },
          data: args,
        })
        if (response.error) {
          return {
            error: axiosErrorMessageHelper(response.error, {
              fallbackError: 'Det skjedde en feil ved opprettelse av etikett',
            }),
          }
        }
        return { data: response.data as LabelModel }
      },
    }),
    updateLabel: build.mutation<
      LabelModel,
      { id: string; updateModel: LabelUpdateModel }
    >({
      invalidatesTags: [GET_LABELS],
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)
        if (!client) throw Error('No client available')
        const response = await query({
          url: baseUrl + `/clients/${client.id}/labels/${args.id}`,
          method: 'PUT',
          headers: { 'x-api-version': '1.0' },
          data: args.updateModel,
        })
        if (response.error) {
          return {
            error: axiosErrorMessageHelper(response.error, {
              fallbackError: 'Det skjedde en feil ved oppdatering av etikett',
            }),
          }
        }
        return { data: response.data as LabelModel }
      },
    }),
    deleteLabel: build.mutation<void, string>({
      invalidatesTags: [GET_LABELS],
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)
        if (!client) throw Error('No client available')
        const response = await query({
          url: baseUrl + `/clients/${client.id}/labels/${args}`,
          method: 'DELETE',
          headers: { 'x-api-version': '1.0' },
        })
        if (response.error) {
          return {
            error: axiosErrorMessageHelper(response.error, {
              fallbackError: 'Det skjedde en feil ved sletting av etikett',
            }),
          }
        }
        return { data: response.data as void }
      },
    }),
    getLabelUsageStats: build.query<LabelUsageStatsModel, { labelId: string }>({
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)
        if (!client) throw Error('No client available')
        const response = await query({
          url:
            baseUrl + `/clients/${client.id}/labels/${args.labelId}/statistics`,
          method: 'GET',
          headers: { 'x-api-version': '1.0' },
        })
        if (response.error) {
          return {
            error: axiosErrorMessageHelper(response.error, {
              fallbackError:
                'Det skjedde en feil ved henting av etikett brukstatistikk',
            }),
          }
        }
        return { data: response.data as LabelUsageStatsModel }
      },
    }),
    updateLabelAndUnitOnFile: build.mutation<
      void,
      { fileId: string; data: BoardDocumentLabelUnitChangeModel }
    >({
      async queryFn(args, api, _extra, query) {
        const state = api.getState() as RootState
        const client = getActiveClient(state)
        if (!client) throw Error('No client available')
        const response = await query({
          url:
            baseUrl +
            `/clients/${client.id}/boarddocuments/${args.fileId}/tags/changes`,
          method: 'PUT',
          headers: { 'x-api-version': '1.0' },
          data: args.data,
        })
        if (response.error) {
          return {
            error: axiosErrorMessageHelper(response.error, {
              fallbackError:
                'Det skjedde en feil ved oppdatering av etikett og enhet på fil',
            }),
          }
        }
        return { data: undefined }
      },
    }),
  }),
})

export const {
  useQueryBoardDocumentsQuery,
  useQueryBoardDocumentsWithSearchTextQuery,
  useQueryBBLDocumentsQuery,
  useGetBBLDocumentTypesQuery,
  useGetBblDocumentQuery,
  useGetFilePageCountQuery,
  useGetFilePagePreviewQuery,
  useGetBBLDocumentsNewCountQuery,
  useUploadBBLDocumentMutation,
  useGetBoardDocumentQuery,
  useDownloadBoardDocumentQuery,
  useDownloadBBLDocumentQuery,
  useUpdateBoardDocumentMutation,
  useGetLabelsQuery,
  useCreateLabelMutation,
  useUpdateLabelMutation,
  useDeleteLabelMutation,
  useGetLabelUsageStatsQuery,
  useUpdateLabelAndUnitOnFileMutation,
  useQueryProsjektarkivDocumentsQuery,
  useQueryProsjektarkivDocumentsWithSearchTextQuery,
  useGetProsjektarkivDocumentQuery,
  useDownloadProsjektarkivDocumentQuery,
  useGetProsjektarkivDocumentTypesQuery,
} = portalDocumentsService

export default portalDocumentsService
